/* © NXAPP Limited */
/**
 * Media Rapid-Response — the newsroom-speed pitch desk.
 *
 * Monitors tech/cyber news, scores stories Asif can comment on, drafts a short
 * personalised pitch to the right journalist, and — only after an explicit
 * approval on the card — sends it and logs it. Nothing sends without approval;
 * there is no bulk / auto path anywhere in this UI.
 *
 * One file, five tabs (mirrors the Leads module's single-file layout):
 *   Approvals · Stories · Contacts · Sources · Log
 */
import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthContext } from '@/context/AuthContext'
import { authedFetch } from '@/lib/authedFetch'
import { describeError } from '@/lib/describeError'
import Spinner from '@/components/ui/Spinner'
import EmptyState from '@/components/ui/EmptyState'
import {
  Radio, Newspaper, Users, Rss, Send, SendHorizonal, Sparkles, RefreshCw, Radar,
  AlertTriangle, ShieldCheck, ShieldAlert, ShieldX, Ban, X, Plus, Trash2, Check,
  Clock, Reply, Award, Mail, ExternalLink, Zap, UserSearch,
} from 'lucide-react'
import { cn } from '@/lib/utils'

// ── Types ─────────────────────────────────────────────────────────────────────
type Tab = 'approvals' | 'stories' | 'contacts' | 'sources' | 'log'

interface Source { id: string; name: string; type: 'rss' | 'gnews' | 'inbound'; url: string | null; active: boolean }
interface Story {
  id: string; headline: string; url: string; outlet: string | null; published_at: string | null
  commentable: boolean | null; topic: string | null; magnitude: number | null; angle: string | null
  sensitive: boolean; summary: string | null; status: 'new' | 'scored' | 'drafted' | 'archived'; created_at: string
  author_name: string | null; author_email: string | null; author_source: string | null
}
interface Contact {
  id: string; name: string | null; outlet: string | null; email: string; beat: string | null
  kind: 'journalist' | 'desk'; status: 'verified' | 'unverified' | 'do-not-contact'
  notes: string | null; last_contacted: string | null; reply_count: number
}
interface Draft {
  id: string; story_id: string; contact_id: string; subject: string; body: string; version: number
  status: string; expires_at: string | null; created_at: string
  media_stories: Pick<Story, 'headline' | 'url' | 'outlet' | 'summary' | 'magnitude' | 'sensitive' | 'topic'> | null
  media_contacts: Pick<Contact, 'name' | 'outlet' | 'email' | 'status' | 'kind' | 'beat' | 'last_contacted' | 'reply_count'> | null
}
interface SendRow {
  id: string; story_id: string | null; contact_id: string | null
  recipient_name: string | null; recipient_email: string; outlet: string | null
  subject: string; status: string; replied: boolean; coverage_won: boolean; created_at: string
}
interface Profile { id: string; name: string; from_name: string; from_email: string; brand_id: string | null; config_enc: string | null }
interface Brand { id: string; name: string }
interface MediaSettings {
  sending_profile_id: string | null; auto_draft: boolean | null; use_desks: boolean | null
  sig_name: string | null; sig_title: string | null; sig_phone: string | null; sig_website: string | null; sig_intro: string | null
  copy_email: string | null
}

// ── Email composer (mirrors functions/lib/media-email.ts so the Preview matches
// exactly what gets sent: greeting + intro + body + signature block) ───────────
// Defaults so the signature is complete before the user saves anything.
const SIG_DEFAULTS = { name: 'Asif Sarangi', title: 'Fractional CTO and AI & cybersecurity adviser', phone: '+44 (0) 7366 247 007', website: 'https://asifsarangi.com/media.html' }
function esc(s: string) { return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') }
function firstNameOf(name: string | null | undefined): string | null {
  const n = (name ?? '').trim(); if (!n) return null
  const f = n.split(/\s+/)[0]; return /^[a-z][a-z'-]+$/i.test(f) ? f : null
}
function composePreviewHtml(body: string, contactName: string | null, sig: MediaSettings, fromEmail: string, article?: { url?: string | null; title?: string | null }, isDesk = false): string {
  const first = firstNameOf(contactName)
  const greeting = first ? `Hi ${first},` : 'Hello,'
  const clean = body.replace(/\n+\s*(best|thanks|thank you|regards|kind regards|cheers|many thanks|warm regards)[,.!]?\s*(\n+.*)?$/i, '').trim()
  const articleLine = isDesk && article?.url
    ? `<p style="margin:0 0 14px">Re: <a href="${esc(article.url)}" style="color:inherit;text-decoration:underline">${esc(article.title?.trim() || article.url)}</a></p>`
    : ''
  const q = clean.replace(/^\s*["“”]+|["“”]+\s*$/g, '').trim()
  const nm = sig.sig_name?.trim() || SIG_DEFAULTS.name
  const cred = sig.sig_title?.trim() || SIG_DEFAULTS.title
  const attribution = `— ${esc(nm)}, ${esc(cred)}`
  const leadIn = `<p style="margin:0 0 14px">Happy to be quoted on this if it helps — here's a line you're welcome to use:</p>`
  const quoteBlock = `<div style="border-left:3px solid #d1d5db;padding:2px 0 2px 14px;margin:0 0 14px"><p style="margin:0">“${esc(q).replace(/\n/g, '<br>')}”</p><p style="margin:8px 0 0;color:#555">${attribution}</p></div>`
  const signOff = `<p style="margin:0 0 14px">Glad to expand or jump on a quick call — mobile below.</p>`
  const href = (s: string) => /^https?:\/\//i.test(s) ? s : `https://${s}`
  const lines: string[] = []
  lines.push(esc(sig.sig_name?.trim() || SIG_DEFAULTS.name))
  if (sig.sig_title?.trim()) lines.push(esc(sig.sig_title.trim()))
  lines.push(esc(sig.sig_phone?.trim() || SIG_DEFAULTS.phone))
  if (fromEmail) lines.push(`<a href="mailto:${esc(fromEmail)}" style="color:inherit;text-decoration:none">${esc(fromEmail)}</a>`)
  const webHref = href(sig.sig_website?.trim() || SIG_DEFAULTS.website)
  const webText = webHref.replace(/\.html?$/i, '')
  lines.push(`<a href="${esc(webHref)}" style="color:inherit;text-decoration:underline">${esc(webText)}</a>`)
  const signature = `<p style="margin:18px 0 0">Best,<br><br>${lines.join('<br>')}</p>`
  return `<div style="font-family:-apple-system,Segoe UI,Arial,sans-serif;font-size:15px;line-height:1.5;color:#111;padding:16px;max-width:600px">` +
    `<p style="margin:0 0 14px">${esc(greeting)}</p>${articleLine}${leadIn}${quoteBlock}${signOff}${signature}</div>`
}

// ── UK default feeds + desk addresses (seedable, one click) ────────────────────
const SEED_SOURCES: Omit<Source, 'id'>[] = [
  { name: 'BBC News — Technology', type: 'rss', url: 'https://feeds.bbci.co.uk/news/technology/rss.xml', active: true },
  { name: 'Sky News — Technology', type: 'rss', url: 'https://feeds.skynews.com/feeds/rss/technology.xml', active: true },
  { name: 'The Guardian — Technology', type: 'rss', url: 'https://www.theguardian.com/uk/technology/rss', active: true },
  { name: 'The Register', type: 'rss', url: 'https://www.theregister.com/headlines.atom', active: true },
  { name: 'TechCrunch', type: 'rss', url: 'https://techcrunch.com/feed/', active: true },
  { name: 'ITPro', type: 'rss', url: 'https://www.itpro.com/feeds/all', active: true },
  { name: 'Computer Weekly', type: 'rss', url: 'https://www.computerweekly.com/rss/All-Computer-Weekly-content.xml', active: true },
  { name: 'City AM', type: 'rss', url: 'https://www.cityam.com/feed/', active: true },
  { name: 'NCSC — News', type: 'rss', url: 'https://www.ncsc.gov.uk/api/1/services/v1/all-rss-feed.xml', active: true },
  { name: 'Google News — "data breach UK"', type: 'gnews', url: 'https://news.google.com/rss/search?q=%22data+breach%22+UK&hl=en-GB&gl=GB&ceid=GB:en', active: true },
  { name: 'Google News — "cyber attack"', type: 'gnews', url: 'https://news.google.com/rss/search?q=%22cyber+attack%22&hl=en-GB&gl=GB&ceid=GB:en', active: true },
  { name: 'Google News — "AI regulation UK"', type: 'gnews', url: 'https://news.google.com/rss/search?q=%22AI+regulation%22+UK&hl=en-GB&gl=GB&ceid=GB:en', active: true },
  { name: 'Google News — "ransomware"', type: 'gnews', url: 'https://news.google.com/rss/search?q=ransomware&hl=en-GB&gl=GB&ceid=GB:en', active: true },
  { name: 'ResponseSource / Qwoted inbox', type: 'inbound', url: null, active: true },
]

// No generic desk addresses are seeded. We pitch only correct, named targets:
// the article's byline author (auto-discovered) and journalists you add
// yourself. Generic desks bounce and hurt the press@ reputation; letters desks
// are the wrong door for quote pitches (see the Letters module).
const SEED_CONTACTS: Omit<Contact, 'id' | 'last_contacted' | 'reply_count'>[] = []

// ── Small helpers ──────────────────────────────────────────────────────────────
function stamp(iso: string | null): string {
  if (!iso) return '—'
  return new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso))
}
function timeLeft(iso: string | null): string {
  if (!iso) return ''
  const ms = Date.parse(iso) - Date.now()
  if (ms <= 0) return 'expired'
  const h = Math.floor(ms / 3_600_000), m = Math.floor((ms % 3_600_000) / 60_000)
  return h > 0 ? `${h}h ${m}m left` : `${m}m left`
}
function MagBadge({ n }: { n: number | null }) {
  if (!n) return null
  const cls = n >= 4 ? 'bg-red-50 text-red-700 border-red-200' : n === 3 ? 'bg-orange-50 text-orange-700 border-orange-200' : 'bg-gray-100 text-gray-600 border-gray-200'
  return <span className={cn('badge border', cls)}>magnitude {n}/5</span>
}
function StatusPill({ s }: { s: Contact['status'] }) {
  if (s === 'verified') return <span className="badge border bg-green-50 text-green-700 border-green-200"><ShieldCheck className="w-3 h-3 mr-1" />verified</span>
  if (s === 'do-not-contact') return <span className="badge border bg-red-50 text-red-700 border-red-200"><ShieldX className="w-3 h-3 mr-1" />do-not-contact</span>
  return <span className="badge border bg-amber-50 text-amber-700 border-amber-200"><ShieldAlert className="w-3 h-3 mr-1" />unverified</span>
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function MediaResponsePage() {
  const { user } = useAuthContext()
  const qc = useQueryClient()
  const [tab, setTab] = useState<Tab>('approvals')
  const [sigOpen, setSigOpen] = useState(false)
  const [testOpen, setTestOpen] = useState(false)
  const [banner, setBanner] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)
  const note = (kind: 'ok' | 'err', text: string) => { setBanner({ kind, text }); setTimeout(() => setBanner(null), 6000) }

  const sourcesQ = useQuery({ queryKey: ['media_sources', user?.id], enabled: !!user, queryFn: async () => {
    const { data, error } = await supabase.from('media_sources').select('*').eq('user_id', user!.id).order('created_at')
    if (error) throw error; return data as Source[]
  } })
  const storiesQ = useQuery({ queryKey: ['media_stories', user?.id], enabled: !!user, queryFn: async () => {
    const { data, error } = await supabase.from('media_stories').select('*').eq('user_id', user!.id).order('created_at', { ascending: false }).limit(300)
    if (error) throw error; return data as Story[]
  } })
  const contactsQ = useQuery({ queryKey: ['media_contacts', user?.id], enabled: !!user, queryFn: async () => {
    const { data, error } = await supabase.from('media_contacts').select('*').eq('user_id', user!.id).order('outlet')
    if (error) throw error; return data as Contact[]
  } })
  const draftsQ = useQuery({ queryKey: ['media_drafts', user?.id], enabled: !!user, queryFn: async () => {
    const { data, error } = await supabase.from('media_drafts')
      .select('*, media_stories(headline,url,outlet,summary,magnitude,sensitive,topic), media_contacts(name,outlet,email,status,kind,beat,last_contacted,reply_count)')
      .eq('user_id', user!.id).eq('status', 'pending').order('created_at', { ascending: false })
    if (error) throw error; return data as Draft[]
  } })
  const sendsQ = useQuery({ queryKey: ['media_sends', user?.id], enabled: !!user, queryFn: async () => {
    const { data, error } = await supabase.from('media_sends').select('*').eq('user_id', user!.id).order('created_at', { ascending: false }).limit(300)
    if (error) throw error; return data as SendRow[]
  } })
  const profilesQ = useQuery({ queryKey: ['sending_profiles', user?.id], enabled: !!user, queryFn: async () => {
    const { data, error } = await supabase.from('sending_profiles').select('id, name, from_name, from_email, brand_id, config_enc').eq('user_id', user!.id).order('name')
    if (error) throw error; return data as Profile[]
  } })
  const brandsQ = useQuery({ queryKey: ['brands', user?.id], enabled: !!user, queryFn: async () => {
    const { data, error } = await supabase.from('brands').select('id, name').eq('user_id', user!.id).order('name')
    if (error) throw error; return data as Brand[]
  } })
  const settingsQ = useQuery({ queryKey: ['media_settings', user?.id], enabled: !!user, queryFn: async () => {
    const { data } = await supabase.from('media_settings')
      .select('sending_profile_id, auto_draft, use_desks, sig_name, sig_title, sig_phone, sig_website, sig_intro, copy_email')
      .eq('user_id', user!.id).maybeSingle()
    return (data as MediaSettings | null) ?? { sending_profile_id: null, auto_draft: true, use_desks: false, sig_name: null, sig_title: null, sig_phone: null, sig_website: null, sig_intro: null, copy_email: null }
  } })

  const invalidate = (...keys: string[]) => keys.forEach(k => qc.invalidateQueries({ queryKey: [k, user?.id] }))

  const chooseProfile = useMutation({
    mutationFn: async (profileId: string | null) => {
      const { error } = await supabase.from('media_settings').upsert(
        { user_id: user!.id, sending_profile_id: profileId, updated_at: new Date().toISOString() }, { onConflict: 'user_id' })
      if (error) throw error
    },
    onSuccess: () => { invalidate('media_settings'); note('ok', 'Sending profile updated for Media pitches.') },
    onError: e => note('err', describeError(e)),
  })
  const toggleAutoDraft = useMutation({
    mutationFn: async (on: boolean) => {
      const { error } = await supabase.from('media_settings').upsert(
        { user_id: user!.id, auto_draft: on, updated_at: new Date().toISOString() }, { onConflict: 'user_id' })
      if (error) throw error
    },
    onSuccess: (_d, on) => { invalidate('media_settings'); note('ok', on ? 'Auto-draft on — pitches drafted automatically for approval.' : 'Auto-draft off — draft manually from the Stories tab.') },
    onError: e => note('err', describeError(e)),
  })
  const toggleDesks = useMutation({
    mutationFn: async (on: boolean) => {
      const { error } = await supabase.from('media_settings').upsert(
        { user_id: user!.id, use_desks: on, updated_at: new Date().toISOString() }, { onConflict: 'user_id' })
      if (error) throw error
    },
    onSuccess: (_d, on) => { invalidate('media_settings'); note('ok', on ? 'Generic desks included in auto-targeting.' : 'Desks off — auto-targeting pitches named journalists only.') },
    onError: e => note('err', describeError(e)),
  })
  const saveSig = useMutation({
    mutationFn: async (sig: Partial<MediaSettings>) => {
      const { error } = await supabase.from('media_settings').upsert(
        { user_id: user!.id, ...sig, updated_at: new Date().toISOString() }, { onConflict: 'user_id' })
      if (error) throw error
    },
    onSuccess: () => { invalidate('media_settings'); note('ok', 'Settings saved.'); setSigOpen(false) },
    onError: e => note('err', describeError(e)),
  })
  const sendTest = useMutation({
    mutationFn: async (toEmail: string) => {
      const r = await authedFetch('/api/media/send-test', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ toEmail }),
      })
      if (!r.ok) throw new Error(await r.text())
      return r.json() as Promise<{ ok: boolean; to: string; via: string; from: string }>
    },
    onSuccess: d => { note('ok', `Test sent to ${d.to} via ${d.via} (from ${d.from}). Check the inbox — and spam.`); setTestOpen(false) },
    onError: e => note('err', `Test failed: ${describeError(e)}`),
  })

  const seed = useMutation({
    mutationFn: async () => {
      // media_sources has no unique(user_id,url) constraint, so we can't upsert
      // on it — insert only the feeds we don't already have. Match on URL, or on
      // name for the URL-less inbound source.
      const { data: existSrc, error: e0 } = await supabase.from('media_sources').select('url, name').eq('user_id', user!.id)
      if (e0) throw e0
      const haveUrl = new Set((existSrc ?? []).map((s: { url: string | null }) => s.url))
      const haveName = new Set((existSrc ?? []).map((s: { name: string }) => s.name))
      const newSrc = SEED_SOURCES.filter(s => (s.url ? !haveUrl.has(s.url) : !haveName.has(s.name)))
      if (newSrc.length) {
        const { error } = await supabase.from('media_sources').insert(newSrc.map(s => ({ ...s, user_id: user!.id })))
        if (error) throw error
      }

      const { data: existC, error: e1 } = await supabase.from('media_contacts').select('email').eq('user_id', user!.id)
      if (e1) throw e1
      const haveEmail = new Set((existC ?? []).map((c: { email: string }) => c.email))
      const newC = SEED_CONTACTS.filter(c => !haveEmail.has(c.email))
      if (newC.length) {
        const { error } = await supabase.from('media_contacts').insert(newC.map(c => ({ ...c, user_id: user!.id })))
        if (error) throw error
      }
      return { sources: newSrc.length, contacts: newC.length }
    },
    onSuccess: (r) => { invalidate('media_sources', 'media_contacts'); note('ok', `Loaded ${r.sources} feed(s) and ${r.contacts} desk address(es).`) },
    onError: e => note('err', describeError(e)),
  })

  const ingest = useMutation({
    mutationFn: async () => {
      const r = await authedFetch('/api/media/ingest', { method: 'POST' })
      if (!r.ok) throw new Error(await r.text())
      return r.json() as Promise<{ ingested: number; skipped: number; sources: number; note?: string }>
    },
    onSuccess: d => { invalidate('media_stories'); note('ok', d.note ?? `Ingested ${d.ingested} new stor${d.ingested === 1 ? 'y' : 'ies'} from ${d.sources} feeds (${d.skipped} already seen).`) },
    onError: e => note('err', describeError(e)),
  })

  // The address pitches send from — shown in the signature as the reply-to.
  const fromEmail = (() => {
    const profs = profilesQ.data ?? []
    const chosen = profs.find(p => p.id === (settingsQ.data?.sending_profile_id ?? ''))
    return chosen?.from_email || profs.find(p => p.from_email.toLowerCase() === 'press@asifsarangi.com')?.from_email || 'press@asifsarangi.com'
  })()

  const pendingCount = draftsQ.data?.length ?? 0
  const newStories = storiesQ.data?.filter(s => s.status === 'new').length ?? 0

  const tabs: { key: Tab; label: string; icon: typeof Radar; badge?: number }[] = [
    { key: 'approvals', label: 'Approvals', icon: SendHorizonal, badge: pendingCount },
    { key: 'stories', label: 'Stories', icon: Newspaper, badge: newStories },
    { key: 'contacts', label: 'Contacts', icon: Users },
    { key: 'sources', label: 'Sources', icon: Rss },
    { key: 'log', label: 'Log', icon: Radio },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Radar className="w-6 h-6 text-brand-600" /> Media Rapid-Response
          </h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            In a journalist's inbox within the hour of a story breaking. The cron ingests, scores and <em>drafts</em> automatically;
            a ready pitch waits in <strong>Approvals</strong> for you to edit and approve. Nothing ever sends without your explicit approval.
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary" onClick={() => setTestOpen(true)}>
            <Send className="w-4 h-4" /> Send test
          </button>
          <button className="btn-secondary" onClick={() => setSigOpen(true)}>
            <Mail className="w-4 h-4" /> Signature
          </button>
          <button className="btn-secondary" onClick={() => seed.mutate()} disabled={seed.isPending}>
            {seed.isPending ? <Spinner className="w-4 h-4" /> : <Plus className="w-4 h-4" />} Load UK defaults
          </button>
          <button className="btn-primary" onClick={() => ingest.mutate()} disabled={ingest.isPending}>
            {ingest.isPending ? <Spinner className="w-4 h-4 border-white border-t-transparent" /> : <RefreshCw className="w-4 h-4" />} Ingest now
          </button>
        </div>
      </div>

      {banner && (
        <div className={cn('rounded-lg border px-4 py-3 text-sm', banner.kind === 'ok' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800')}>
          {banner.text}
        </div>
      )}

      {/* Sending profile + brand — same concept as campaigns/issues */}
      {(() => {
        const profiles = profilesQ.data ?? []
        const brands = brandsQ.data ?? []
        const chosenId = settingsQ.data?.sending_profile_id ?? ''
        const chosen = profiles.find(p => p.id === chosenId)
        const brandName = chosen?.brand_id ? brands.find(b => b.id === chosen.brand_id)?.name : null
        const pressFallback = profiles.find(p => p.from_email.toLowerCase() === 'press@asifsarangi.com')
        return (
          <div className="card px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
            <span className="flex items-center gap-2 text-gray-500"><Send className="w-4 h-4" /> Pitches send from</span>
            <select
              className="input max-w-xs py-1.5"
              value={chosenId}
              onChange={e => chooseProfile.mutate(e.target.value || null)}
              disabled={chooseProfile.isPending}
            >
              <option value="">
                {pressFallback ? `Press default (${pressFallback.from_email})` : 'Press default (press@asifsarangi.com)'}
              </option>
              {profiles.map(p => (
                <option key={p.id} value={p.id}>
                  {p.from_name} &lt;{p.from_email}&gt;{p.brand_id ? ` · ${brands.find(b => b.id === p.brand_id)?.name ?? 'Brand'}` : ''}
                </option>
              ))}
            </select>
            {brandName && <span className="badge bg-brand-50 text-brand-700 border border-brand-200">{brandName}</span>}
            {chosen && !chosen.config_enc && (
              <span className="text-xs text-amber-600 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> this profile has no credentials — sends will fail</span>
            )}
            {!profiles.length && (
              <span className="text-xs text-gray-400">No sending profiles yet — add one in Settings, or leave on the press default.</span>
            )}

            {/* BCC status — so it's obvious whether a copy goes to you */}
            {settingsQ.data?.copy_email
              ? <span className="badge bg-gray-100 text-gray-600" title="Every sent pitch is BCC'd here">BCC → {settingsQ.data.copy_email}</span>
              : <button onClick={() => setSigOpen(true)} className="text-xs text-amber-600 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> BCC not set — no copy reaches you</button>}

            {/* Desk targeting — off = pitch named journalists only (no generic-desk blasts) */}
            <span className="ml-auto flex items-center gap-2 text-gray-500" title="Off = pitch only the byline author + journalists you've added. On = also allow generic desk addresses.">
              <Users className="w-4 h-4" /> Pitch desks
              <button
                onClick={() => toggleDesks.mutate(!(settingsQ.data?.use_desks ?? false))}
                disabled={toggleDesks.isPending}
                className={cn('w-9 h-5 rounded-full relative transition-colors shrink-0', (settingsQ.data?.use_desks ?? false) ? 'bg-brand-600' : 'bg-gray-300')}
              >
                <span className={cn('absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all', (settingsQ.data?.use_desks ?? false) ? 'left-4' : 'left-0.5')} />
              </button>
            </span>

            {/* Auto-draft toggle — on = the cron drafts a pitch for every commentable story */}
            <span className="flex items-center gap-2 text-gray-500">
              <Zap className="w-4 h-4" /> Auto-draft
              <button
                onClick={() => toggleAutoDraft.mutate(!(settingsQ.data?.auto_draft ?? true))}
                disabled={toggleAutoDraft.isPending}
                className={cn('w-9 h-5 rounded-full relative transition-colors shrink-0', (settingsQ.data?.auto_draft ?? true) ? 'bg-brand-600' : 'bg-gray-300')}
                title={(settingsQ.data?.auto_draft ?? true) ? 'On — pitches drafted automatically' : 'Off — draft manually'}
              >
                <span className={cn('absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all', (settingsQ.data?.auto_draft ?? true) ? 'left-4' : 'left-0.5')} />
              </button>
            </span>
          </div>
        )
      })()}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={cn('flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors',
              tab === t.key ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-800')}>
            <t.icon className="w-4 h-4" /> {t.label}
            {!!t.badge && <span className="badge bg-brand-100 text-brand-700">{t.badge}</span>}
          </button>
        ))}
      </div>

      {tab === 'approvals' && <ApprovalsTab drafts={draftsQ.data ?? []} loading={draftsQ.isLoading} userId={user!.id} sig={settingsQ.data ?? null} fromEmail={fromEmail} invalidate={invalidate} note={note} />}
      {tab === 'stories' && <StoriesTab stories={storiesQ.data ?? []} contacts={contactsQ.data ?? []} loading={storiesQ.isLoading} userId={user!.id} invalidate={invalidate} note={note} />}
      {tab === 'contacts' && <ContactsTab contacts={contactsQ.data ?? []} loading={contactsQ.isLoading} userId={user!.id} invalidate={invalidate} note={note} />}
      {tab === 'sources' && <SourcesTab sources={sourcesQ.data ?? []} loading={sourcesQ.isLoading} userId={user!.id} invalidate={invalidate} note={note} />}
      {tab === 'log' && <LogTab sends={sendsQ.data ?? []} loading={sendsQ.isLoading} userId={user!.id} invalidate={invalidate} note={note} />}

      {sigOpen && (
        <SignatureModal
          initial={settingsQ.data ?? null}
          saving={saveSig.isPending}
          fromEmail={fromEmail}
          onCancel={() => setSigOpen(false)}
          onSave={sig => saveSig.mutate(sig)}
        />
      )}

      {testOpen && (
        <TestModal
          defaultEmail={settingsQ.data?.copy_email || user?.email || ''}
          fromEmail={fromEmail}
          sending={sendTest.isPending}
          onCancel={() => setTestOpen(false)}
          onSend={to => sendTest.mutate(to)}
        />
      )}
    </div>
  )
}

// ── Test send ─────────────────────────────────────────────────────────────────
function TestModal({ defaultEmail, fromEmail, sending, onCancel, onSend }: {
  defaultEmail: string; fromEmail: string; sending: boolean; onCancel: () => void; onSend: (to: string) => void
}) {
  const [to, setTo] = useState(defaultEmail)
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="card w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()}>
        <h3 className="font-semibold text-gray-900">Send a test pitch</h3>
        <p className="text-sm text-gray-500">Sends a sample pitch through the exact profile a real send uses (from <span className="font-medium text-gray-700">{fromEmail}</span>), so you can confirm your sending profile works. If it fails, you'll get the provider's error.</p>
        <div><label className="label">Send test to</label><input className="input" value={to} onChange={e => setTo(e.target.value)} placeholder="you@example.com" /></div>
        <div className="flex justify-end gap-2">
          <button className="btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn-primary" onClick={() => onSend(to.trim())} disabled={!to.trim() || sending}>
            {sending ? <Spinner className="w-4 h-4 border-white border-t-transparent" /> : <Send className="w-4 h-4" />} Send test
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Signature editor ──────────────────────────────────────────────────────────
function SignatureModal({ initial, fromEmail, saving, onCancel, onSave }: {
  initial: MediaSettings | null; fromEmail: string; saving: boolean; onCancel: () => void; onSave: (sig: Partial<MediaSettings>) => void
}) {
  const [f, setF] = useState({
    sig_name: initial?.sig_name ?? 'Asif Sarangi',
    sig_title: initial?.sig_title ?? 'Fractional CTO and AI & cybersecurity adviser',
    sig_phone: initial?.sig_phone ?? '+44 (0) 7366 247 007',
    sig_website: initial?.sig_website ?? 'https://asifsarangi.com/media.html',
    sig_intro: initial?.sig_intro ?? '',
    copy_email: initial?.copy_email ?? '',
  })
  const set = (k: keyof typeof f, v: string) => setF(s => ({ ...s, [k]: v }))
  const preview = composePreviewHtml(
    'This is exactly the kind of risk UK firms keep underestimating — the fix is boring but it works, and most boards still won’t fund it until they’re breached.',
    'Jane', { sending_profile_id: null, auto_draft: true, ...f } as MediaSettings, fromEmail,
    { url: 'https://example.com/the-breach-story', title: 'Major UK retailer hit by data breach' },
  )
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="card w-full max-w-2xl max-h-[88vh] overflow-y-auto p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Email signature</h3>
          <button onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-gray-500">Every pitch is topped with a greeting and closed with this block, in the same font as the email. Your sending address <span className="font-medium text-gray-700">{fromEmail}</span> is shown automatically as the contact email.</p>

        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className="label">Name</label><input className="input" value={f.sig_name} onChange={e => set('sig_name', e.target.value)} placeholder="Asif Sarangi" /></div>
          <div><label className="label">Mobile</label><input className="input" value={f.sig_phone} onChange={e => set('sig_phone', e.target.value)} placeholder="+44 7…" /></div>
          <div><label className="label">Website / media page</label><input className="input" value={f.sig_website} onChange={e => set('sig_website', e.target.value)} placeholder="https://asifsarangi.com/media.html" /><p className="text-xs text-gray-400 mt-1">Links to this; the visible text hides any .html.</p></div>
        </div>
        <div><label className="label">Title / credentials <span className="text-amber-600 font-normal">— important</span></label><input className="input" value={f.sig_title} onChange={e => set('sig_title', e.target.value)} placeholder="Fractional CTO and AI & cybersecurity adviser" /><p className="text-xs text-gray-400 mt-1">This is how a journalist attributes your quote — lead with the expertise relevant to the story (AI, cybersecurity).</p></div>

        <div><label className="label">Blind-copy me on every pitch (BCC)</label><input className="input" value={f.copy_email} onChange={e => set('copy_email', e.target.value)} placeholder="vip@asifsarangi.com" /><p className="text-xs text-gray-400 mt-1">You get a copy in your inbox; the journalist never sees it. Leave blank for none.</p></div>

        <div>
          <p className="label">Preview</p>
          <div className="border border-gray-200 rounded-lg overflow-hidden bg-white h-56">
            <iframe srcDoc={preview} className="w-full h-full" sandbox="" title="Signature preview" />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button className="btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn-primary" onClick={() => onSave(f)} disabled={saving}>
            {saving ? <Spinner className="w-4 h-4 border-white border-t-transparent" /> : <Check className="w-4 h-4" />} Save
          </button>
        </div>
      </div>
    </div>
  )
}

type Note = (kind: 'ok' | 'err', text: string) => void
type Invalidate = (...keys: string[]) => void

// ── Approvals (the gate) ──────────────────────────────────────────────────────
function ApprovalsTab({ drafts, loading, userId, sig, fromEmail, invalidate, note }: { drafts: Draft[]; loading: boolean; userId: string; sig: MediaSettings | null; fromEmail: string; invalidate: Invalidate; note: Note }) {
  if (loading) return <div className="py-16 text-center"><Spinner className="w-6 h-6 mx-auto" /></div>
  // Surface only pitches that (a) have a recipient email and (b) haven't expired
  // (4h window). Expired ones can't go out; the cron flips them to 'expired'.
  const shown = drafts.filter(d => !!d.media_contacts?.email?.trim() && (!d.expires_at || Date.parse(d.expires_at) > Date.now()))
  if (!shown.length) return <EmptyState icon={SendHorizonal} title="Nothing waiting for approval" description="With auto-draft on, stories with a known journalist email are drafted for you and appear here. You can also draft manually from the Stories tab." />
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500 flex items-center gap-2"><Clock className="w-4 h-4" /> Pending pitches expire 4 hours after drafting — a stale pitch hurts more than silence.</p>
      {shown.map(d => <ApprovalCard key={d.id} draft={d} userId={userId} sig={sig} fromEmail={fromEmail} invalidate={invalidate} note={note} />)}
    </div>
  )
}

const DEFAULT_SIG: MediaSettings = { sending_profile_id: null, auto_draft: true, use_desks: false, sig_name: null, sig_title: null, sig_phone: null, sig_website: null, sig_intro: null, copy_email: null }

function ApprovalCard({ draft, userId, sig, fromEmail, invalidate, note }: { draft: Draft; userId: string; sig: MediaSettings | null; fromEmail: string; invalidate: Invalidate; note: Note }) {
  const [subject, setSubject] = useState(draft.subject)
  const [body, setBody] = useState(draft.body)
  const [view, setView] = useState<'edit' | 'preview'>('edit')
  const [sent, setSent] = useState(false)
  const edited = subject !== draft.subject || body !== draft.body
  const story = draft.media_stories
  const contact = draft.media_contacts

  // Why a send might be refused, computed up front so we can explain it and
  // disable the button rather than let the click fail server-side.
  const cappedUntil = contact?.last_contacted && (contact.reply_count ?? 0) === 0
    ? Date.parse(contact.last_contacted) + 7 * 24 * 60 * 60 * 1000 : 0
  const blockReason = contact?.status === 'do-not-contact'
    ? 'This contact is marked do-not-contact — sending is permanently blocked.'
    : cappedUntil > Date.now()
      ? `Already emailed ${contact?.email} in the last 7 days (no reply yet). The frequency cap lifts ${stamp(new Date(cappedUntil).toISOString())}.`
      : null

  const send = useMutation({
    mutationFn: async () => {
      const r = await authedFetch('/api/media/send', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ draftId: draft.id, subject, body }),
      })
      if (!r.ok) throw new Error(await r.text())
      return r.json()
    },
    onSuccess: () => { setSent(true); invalidate('media_drafts', 'media_sends', 'media_stories', 'media_contacts'); note('ok', `Sent to ${contact?.email} — moved to the Log.`) },
    onError: e => note('err', describeError(e)),
  })
  const skip = useMutation({
    mutationFn: async () => { const { error } = await supabase.from('media_drafts').update({ status: 'skipped' }).eq('id', draft.id).eq('user_id', userId); if (error) throw error },
    onSuccess: () => { invalidate('media_drafts'); note('ok', 'Skipped.') },
    onError: e => note('err', describeError(e)),
  })
  const never = useMutation({
    mutationFn: async () => {
      await supabase.from('media_drafts').update({ status: 'skipped' }).eq('id', draft.id).eq('user_id', userId)
      const { error } = await supabase.from('media_contacts').update({ status: 'do-not-contact' }).eq('id', draft.contact_id).eq('user_id', userId)
      if (error) throw error
    },
    onSuccess: () => { invalidate('media_drafts', 'media_contacts'); note('ok', 'Contact marked do-not-contact — permanent.') },
    onError: e => note('err', describeError(e)),
  })

  return (
    <div className={cn('card p-5 space-y-4', story?.sensitive && 'ring-2 ring-red-200')}>
      {/* Story header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            {story?.sensitive && <span className="badge border bg-red-100 text-red-800 border-red-300"><AlertTriangle className="w-3 h-3 mr-1" />SENSITIVE — sober tone</span>}
            <MagBadge n={story?.magnitude ?? null} />
            {story?.topic && <span className="badge bg-gray-100 text-gray-600">{story.topic}</span>}
          </div>
          <a href={story?.url} target="_blank" rel="noreferrer" className="font-semibold text-gray-900 hover:text-brand-700 inline-flex items-center gap-1">
            {story?.headline} <ExternalLink className="w-3.5 h-3.5 shrink-0" />
          </a>
          {story?.summary && <p className="text-sm text-gray-500 mt-1">{story.summary}</p>}
        </div>
        <span className="text-xs text-gray-400 whitespace-nowrap">{timeLeft(draft.expires_at)}</span>
      </div>

      {/* Recipient */}
      <div className="flex flex-wrap items-center gap-2 text-sm bg-gray-50 rounded-lg px-3 py-2">
        <Mail className="w-4 h-4 text-gray-400" />
        <span className="font-medium text-gray-800">{contact?.name || contact?.outlet || 'Desk'}</span>
        {contact?.beat?.toLowerCase().includes('byline') && <span className="badge bg-brand-50 text-brand-700 border border-brand-200">✍ article author</span>}
        <span className="text-gray-400">·</span>
        <span className="text-gray-700 font-medium">{contact?.email}</span>
        {contact?.outlet && contact?.name && <span className="text-gray-400">({contact.outlet})</span>}
        {contact && <StatusPill s={contact.status} />}
        {contact?.status === 'unverified' && <span className="text-xs text-amber-600">— check the address, then send</span>}
      </div>

      {/* Editable draft + live preview */}
      <div className="space-y-2">
        <div>
          <label className="label">Subject</label>
          <input className="input" value={subject} onChange={e => setSubject(e.target.value)} />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="label mb-0">Quote — what they'll publish {edited && <span className="text-brand-600 text-xs">· edited</span>}</label>
            <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden text-xs">
              <button onClick={() => setView('edit')} className={cn('px-3 py-1', view === 'edit' ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-500 hover:bg-gray-50')}>Edit</button>
              <button onClick={() => setView('preview')} className={cn('px-3 py-1 border-l border-gray-200', view === 'preview' ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-500 hover:bg-gray-50')}>Preview</button>
            </div>
          </div>
          {view === 'edit' ? (
            <textarea className="input min-h-[160px] font-mono text-[13px] leading-relaxed" value={body} onChange={e => setBody(e.target.value)} />
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white h-72">
              <iframe
                srcDoc={composePreviewHtml(body, contact?.name ?? null, sig ?? DEFAULT_SIG, fromEmail, story ? { url: story.url, title: story.headline } : undefined, contact?.kind === 'desk')}
                className="w-full h-full" sandbox="" title="Email preview"
              />
            </div>
          )}
          <p className="text-xs text-gray-400 mt-1">{body.trim().split(/\s+/).filter(Boolean).length} words · this is the attributable quote · greeting, "you're welcome to quote this", attribution + signature are added around it — see Preview</p>
        </div>
      </div>

      {/* Gate buttons — replaced by a Sent confirmation once it's gone out, so
          there's no way to send the same pitch twice. */}
      {sent ? (
        <div className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
          <Check className="w-4 h-4" /> Sent to {contact?.email}. It's in the <strong>Log</strong> now — no further action needed.
        </div>
      ) : (
        <>
          {blockReason && (
            <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800 mb-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> <span>Can't send: {blockReason} Skip it, or send once they reply.</span>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <button className="btn-primary" onClick={() => send.mutate()} disabled={send.isPending || !!blockReason} title={blockReason ?? ''}>
              {send.isPending ? <Spinner className="w-4 h-4 border-white border-t-transparent" /> : <Send className="w-4 h-4" />}
              {send.isPending ? 'Sending…' : edited ? 'Edit & Send' : 'Send'}
            </button>
            <button className="btn-secondary" onClick={() => skip.mutate()} disabled={skip.isPending}>Skip</button>
            <button className="btn-secondary text-red-600 hover:bg-red-50" onClick={() => { if (confirm('Permanently mark this contact do-not-contact?')) never.mutate() }} disabled={never.isPending}>
              <Ban className="w-4 h-4" /> Never this contact
            </button>
          </div>
        </>
      )}
    </div>
  )
}

// ── Stories ───────────────────────────────────────────────────────────────────
function StoriesTab({ stories, contacts, loading, userId, invalidate, note }: { stories: Story[]; contacts: Contact[]; loading: boolean; userId: string; invalidate: Invalidate; note: Note }) {
  const [filter, setFilter] = useState<'live' | 'new' | 'scored' | 'archived'>('live')
  const [withAuthor, setWithAuthor] = useState(true)
  const [pick, setPick] = useState<Story | null>(null)

  // "Live" = the last 7 days of non-archived news — a rapid-response desk only
  // cares about fresh stories. Older ones are still reachable via the status
  // filters, and the cron deletes anything past 30 days. "With author" (default)
  // then narrows to stories where we found a journalist to email.
  const recentCut = Date.now() - 7 * 24 * 60 * 60 * 1000
  const shown = useMemo(() => stories.filter(s => {
    const inFilter = filter === 'live'
      ? s.status !== 'archived' && Date.parse(s.published_at ?? s.created_at) > recentCut
      : s.status === filter
    return inFilter && (!withAuthor || !!s.author_email)
  }), [stories, filter, withAuthor, recentCut])

  const scoreNew = useMutation({
    mutationFn: async () => {
      const ids = stories.filter(s => s.status === 'new').map(s => s.id).slice(0, 25)
      if (!ids.length) throw new Error('No new stories to score.')
      const r = await authedFetch('/api/media/score', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ storyIds: ids }) })
      if (!r.ok) throw new Error(await r.text())
      return r.json() as Promise<{ scored: number; archived: number }>
    },
    onSuccess: d => { invalidate('media_stories'); note('ok', `Scored ${d.scored} commentable, ${d.archived} set aside.`) },
    onError: e => note('err', describeError(e)),
  })
  const [findingId, setFindingId] = useState<string | null>(null)
  const [draftingId, setDraftingId] = useState<string | null>(null)
  const findAuthor = useMutation({
    mutationFn: async (storyId: string) => {
      const r = await authedFetch('/api/media/byline', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ storyId }) })
      if (!r.ok) throw new Error(await r.text())
      return r.json() as Promise<{ author_name: string | null; author_email: string | null; author_source: string | null }>
    },
    onSuccess: d => { invalidate('media_stories'); note(d.author_email ? 'ok' : 'err', d.author_email ? `Found ${d.author_name || 'author'}: ${d.author_email}` : 'No author email found on this article.') },
    onSettled: () => setFindingId(null),
    onError: e => note('err', describeError(e)),
  })
  const autoDraft = useMutation({
    mutationFn: async (storyId: string) => {
      const r = await authedFetch('/api/media/draft', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ storyId, auto: true }) })
      if (!r.ok) throw new Error(await r.text())
      return r.json() as Promise<{ drafted: unknown[]; skipped: { email: string; reason: string }[] }>
    },
    onSuccess: (d, storyId) => {
      invalidate('media_drafts', 'media_stories')
      if (d.drafted?.length) { note('ok', `${d.drafted.length} pitch${d.drafted.length === 1 ? '' : 'es'} drafted — in Approvals.`); return }
      // No auto recipient (author blocked/none) — open the picker so you can
      // choose an existing contact or add/correct a recipient.
      const story = stories.find(s => s.id === storyId)
      if (story) setPick(story)
      note('err', 'No auto recipient available — choose or add someone.')
    },
    onSettled: () => setDraftingId(null),
    onError: e => note('err', describeError(e)),
  })

  if (loading) return <div className="py-16 text-center"><Spinner className="w-6 h-6 mx-auto" /></div>

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3 text-sm">
          <div className="flex gap-1">
            {(['live', 'new', 'scored', 'archived'] as const).map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={cn('px-3 py-1.5 rounded-lg capitalize', filter === f ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-500 hover:bg-gray-100')}>{f}</button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-gray-500 cursor-pointer" title="Only stories where we found a journalist to email">
            With author
            <button type="button" onClick={() => setWithAuthor(v => !v)}
              className={cn('w-9 h-5 rounded-full relative transition-colors shrink-0', withAuthor ? 'bg-brand-600' : 'bg-gray-300')}>
              <span className={cn('absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all', withAuthor ? 'left-4' : 'left-0.5')} />
            </button>
          </label>
        </div>
        <button className="btn-secondary" onClick={() => scoreNew.mutate()} disabled={scoreNew.isPending}>
          {scoreNew.isPending ? <Spinner className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />} Score new stories
        </button>
      </div>

      {!shown.length ? (
        withAuthor
          ? <EmptyState icon={UserSearch} title="No stories with a known author yet" description="These show once scoring has found a journalist to email. Toggle “With author” off to see everything, or hit “Score new stories”." />
          : <EmptyState icon={Newspaper} title="No stories here yet" description="Hit “Ingest now” to pull the feeds, then “Score new stories” to triage them." />
      ) : (
        <div className="space-y-2">
          {shown.map(s => (
            <div key={s.id} className={cn('card p-4', s.sensitive && 'ring-1 ring-red-200')}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    {s.status === 'new' && <span className="badge bg-gray-100 text-gray-500">unscored</span>}
                    {s.status === 'scored' && <span className="badge bg-green-50 text-green-700">commentable</span>}
                    {s.status === 'drafted' && <span className="badge bg-brand-50 text-brand-700">drafted</span>}
                    {s.status === 'archived' && <span className="badge bg-gray-100 text-gray-400">silent</span>}
                    {s.sensitive && <span className="badge border bg-red-100 text-red-800 border-red-300"><AlertTriangle className="w-3 h-3 mr-1" />SENSITIVE</span>}
                    <MagBadge n={s.magnitude} />
                    {s.topic && <span className="badge bg-gray-100 text-gray-600">{s.topic}</span>}
                    <span className="text-xs text-gray-400">{s.outlet} · {stamp(s.published_at ?? s.created_at)}</span>
                  </div>
                  <a href={s.url} target="_blank" rel="noreferrer" className="font-medium text-gray-900 hover:text-brand-700 inline-flex items-center gap-1">
                    {s.headline} <ExternalLink className="w-3.5 h-3.5 shrink-0 text-gray-400" />
                  </a>
                  {s.angle && <p className="text-sm text-gray-600 mt-1"><span className="text-gray-400">Angle:</span> {s.angle}</p>}

                  {/* Who we'd pitch — shown before you draft */}
                  {(s.status === 'scored' || s.status === 'drafted') && (
                    <div className="mt-1.5 text-xs flex flex-wrap items-center gap-1.5">
                      {s.author_email ? (
                        <>
                          <span className="badge bg-brand-50 text-brand-700 border border-brand-200">✍ {s.author_name || 'Author'}</span>
                          <a href={`mailto:${s.author_email}`} className="text-brand-700 font-medium hover:underline">{s.author_email}</a>
                          {s.author_source === 'derived' && <span className="text-amber-600">(derived — verify)</span>}
                          <button className="text-gray-400 hover:text-gray-700 inline-flex items-center gap-0.5" title="Re-check author" onClick={() => { setFindingId(s.id); findAuthor.mutate(s.id) }} disabled={findingId === s.id}>
                            {findingId === s.id ? <Spinner className="w-3 h-3" /> : <UserSearch className="w-3 h-3" />}
                          </button>
                        </>
                      ) : s.author_source ? (
                        <span className="text-gray-400 inline-flex items-center gap-1.5">
                          {s.author_name ? `Author: ${s.author_name} — no email found` : 'No author email found'}
                          <button className="text-gray-400 hover:text-gray-700" title="Re-check" onClick={() => { setFindingId(s.id); findAuthor.mutate(s.id) }} disabled={findingId === s.id}>
                            {findingId === s.id ? <Spinner className="w-3 h-3" /> : <UserSearch className="w-3 h-3" />}
                          </button>
                        </span>
                      ) : (
                        <button className="text-brand-600 hover:underline inline-flex items-center gap-1 disabled:opacity-50" onClick={() => { setFindingId(s.id); findAuthor.mutate(s.id) }} disabled={findingId === s.id}>
                          {findingId === s.id ? <Spinner className="w-3 h-3" /> : <UserSearch className="w-3 h-3" />} Find author
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {s.status === 'scored' && (
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <button className="btn-primary" onClick={() => { setDraftingId(s.id); autoDraft.mutate(s.id) }} disabled={draftingId === s.id} title="Draft to the article author + any matched journalists">
                      {draftingId === s.id ? <Spinner className="w-4 h-4 border-white border-t-transparent" /> : <Zap className="w-4 h-4" />} Draft pitch
                    </button>
                    <button className="text-xs text-gray-400 hover:text-gray-700" onClick={() => setPick(s)}>choose recipients…</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {pick && <PitchPicker story={pick} contacts={contacts} userId={userId} onClose={() => setPick(null)} invalidate={invalidate} note={note} />}
    </div>
  )
}

// Contact picker modal — choose up to 4, generate a personalised draft each.
function PitchPicker({ story, contacts, onClose, invalidate, note }: { story: Story; contacts: Contact[]; userId: string; onClose: () => void; invalidate: Invalidate; note: Note }) {
  const [sel, setSel] = useState<string[]>([])
  const [extras, setExtras] = useState<{ name: string; email: string }[]>([])
  // Pre-fill with the discovered author so you can correct a bounced address.
  const [nn, setNn] = useState(story.author_name ?? '')
  const [ne, setNe] = useState(story.author_email ?? '')
  const addExtra = () => {
    const email = ne.trim().toLowerCase()
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { note('err', 'Enter a valid email.'); return }
    if (extras.some(e => e.email === email)) return
    setExtras(e => [...e, { name: nn.trim(), email }]); setNn(''); setNe('')
  }
  const eligible = contacts.filter(c => c.status !== 'do-not-contact')
  // Suggest beat/topic matches first.
  const ranked = useMemo(() => [...eligible].sort((a, b) => {
    const match = (c: Contact) => (c.beat && story.topic && c.beat.toLowerCase().includes(story.topic.split('/')[0].toLowerCase())) ? 0 : 1
    return match(a) - match(b)
  }), [eligible, story.topic])

  const toggle = (id: string) => setSel(s => s.includes(id) ? s.filter(x => x !== id) : s.length >= 4 ? s : [...s, id])

  const gen = useMutation({
    mutationFn: async () => {
      const r = await authedFetch('/api/media/draft', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ storyId: story.id, contactIds: sel, newContacts: extras }) })
      if (!r.ok) throw new Error(await r.text())
      return r.json() as Promise<{ drafted: unknown[]; skipped: { email: string; reason: string }[] }>
    },
    onSuccess: d => {
      invalidate('media_drafts', 'media_stories', 'media_contacts')
      const skips = d.skipped.length ? ` (${d.skipped.length} skipped: ${d.skipped.map(s => s.reason).join(', ')})` : ''
      note('ok', `${d.drafted.length} draft${d.drafted.length === 1 ? '' : 's'} ready in Approvals${skips}.`)
      onClose()
    },
    onError: e => note('err', describeError(e)),
  })

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="card w-full max-w-lg max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h3 className="font-semibold text-gray-900">Draft pitch — pick up to 4</h3>
            <p className="text-xs text-gray-500 truncate max-w-md">{story.headline}</p>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          {/* Add / correct a recipient (pre-filled with the article author). */}
          <div className="rounded-lg border border-gray-200 p-3 mb-2 space-y-2">
            <p className="text-xs font-medium text-gray-600">Pitch a specific journalist — add or correct their email</p>
            <div className="flex flex-wrap gap-2">
              <input className="input flex-1 min-w-[7rem] py-1.5" placeholder="Name" value={nn} onChange={e => setNn(e.target.value)} />
              <input className="input flex-1 min-w-[10rem] py-1.5" placeholder="email@outlet.com" value={ne} onChange={e => setNe(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addExtra() }} />
              <button className="btn-secondary py-1.5" onClick={addExtra}><Plus className="w-4 h-4" /> Add</button>
            </div>
            {!!extras.length && (
              <div className="flex flex-wrap gap-1.5">
                {extras.map((e, i) => (
                  <span key={e.email} className="badge bg-brand-50 text-brand-700 border border-brand-200 gap-1">
                    {e.name || e.email}<button onClick={() => setExtras(x => x.filter((_, j) => j !== i))} className="text-brand-400 hover:text-brand-700"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
          {!!ranked.length && <p className="text-xs text-gray-400 px-1">…or pick from your contacts:</p>}
          {ranked.map(c => {
            const on = sel.includes(c.id)
            const disabled = !on && sel.length >= 4
            return (
              <button key={c.id} onClick={() => toggle(c.id)} disabled={disabled}
                className={cn('w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left text-sm', on ? 'bg-brand-50 ring-1 ring-brand-300' : 'hover:bg-gray-50', disabled && 'opacity-40 cursor-not-allowed')}>
                <span className={cn('w-4 h-4 rounded border flex items-center justify-center shrink-0', on ? 'bg-brand-600 border-brand-600' : 'border-gray-300')}>{on && <Check className="w-3 h-3 text-white" />}</span>
                <span className="min-w-0 flex-1">
                  <span className="font-medium text-gray-800">{c.name || c.outlet || c.email}</span>
                  <span className="text-gray-400 ml-1">{c.email}</span>
                  {c.beat && <span className="block text-xs text-gray-400">{c.beat}</span>}
                </span>
                <StatusPill s={c.status} />
              </button>
            )
          })}
        </div>
        <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-gray-100">
          <span className="text-xs text-gray-500">{sel.length + extras.length} recipient(s) · each gets its own personalised email</span>
          <button className="btn-primary" onClick={() => gen.mutate()} disabled={(!sel.length && !extras.length) || gen.isPending}>
            {gen.isPending ? <Spinner className="w-4 h-4 border-white border-t-transparent" /> : <Sparkles className="w-4 h-4" />} Generate drafts
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Contacts ──────────────────────────────────────────────────────────────────
const BLANK_CONTACT = { name: '', outlet: '', email: '', beat: '', kind: 'journalist' as const, status: 'unverified' as const, notes: '' }

function ContactsTab({ contacts, loading, userId, invalidate, note }: { contacts: Contact[]; loading: boolean; userId: string; invalidate: Invalidate; note: Note }) {
  const [form, setForm] = useState<typeof BLANK_CONTACT | null>(null)

  const save = useMutation({
    mutationFn: async (c: typeof BLANK_CONTACT) => {
      if (!c.email.trim()) throw new Error('Email is required.')
      const { error } = await supabase.from('media_contacts').upsert({
        user_id: userId, name: c.name || null, outlet: c.outlet || null, email: c.email.trim().toLowerCase(),
        beat: c.beat || null, kind: c.kind, status: c.status, notes: c.notes || null,
      }, { onConflict: 'user_id,email' })
      if (error) throw error
    },
    onSuccess: () => { invalidate('media_contacts'); note('ok', 'Contact saved.'); setForm(null) },
    onError: e => note('err', describeError(e)),
  })
  const setStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: Contact['status'] }) => {
      const { error } = await supabase.from('media_contacts').update({ status }).eq('id', id).eq('user_id', userId); if (error) throw error
    },
    onSuccess: () => invalidate('media_contacts'), onError: e => note('err', describeError(e)),
  })
  const del = useMutation({
    mutationFn: async (id: string) => { const { error } = await supabase.from('media_contacts').delete().eq('id', id).eq('user_id', userId); if (error) throw error },
    onSuccess: () => { invalidate('media_contacts'); note('ok', 'Contact removed.') }, onError: e => note('err', describeError(e)),
  })

  if (loading) return <div className="py-16 text-center"><Spinner className="w-6 h-6 mx-auto" /></div>

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">{contacts.length} contact{contacts.length === 1 ? '' : 's'}. Byline & beat matches are suggested first when drafting.</p>
        <button className="btn-secondary" onClick={() => setForm({ ...BLANK_CONTACT })}><Plus className="w-4 h-4" /> Add contact</button>
      </div>

      {!contacts.length ? (
        <EmptyState icon={Users} title="No journalists yet" description="Load the UK defaults for desk addresses, or add journalists you know here." />
      ) : (
        <div className="card divide-y divide-gray-100">
          {contacts.map(c => (
            <div key={c.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">{c.name || <span className="text-gray-400 italic">Desk</span>}</span>
                  {c.kind === 'desk' && <span className="badge bg-gray-100 text-gray-500">desk</span>}
                  <StatusPill s={c.status} />
                </div>
                <div className="text-gray-500">{c.email} {c.outlet && `· ${c.outlet}`} {c.beat && `· ${c.beat}`}</div>
                {(c.last_contacted || c.reply_count > 0) && (
                  <div className="text-xs text-gray-400 mt-0.5">
                    {c.last_contacted && `last contacted ${stamp(c.last_contacted)}`}{c.reply_count > 0 && ` · ${c.reply_count} repl${c.reply_count === 1 ? 'y' : 'ies'}`}
                  </div>
                )}
              </div>
              <div className="flex gap-1">
                {c.status !== 'verified' && <button className="btn-secondary py-1 px-2" title="Verify" onClick={() => setStatus.mutate({ id: c.id, status: 'verified' })}><ShieldCheck className="w-4 h-4 text-green-600" /></button>}
                {c.status !== 'do-not-contact'
                  ? <button className="btn-secondary py-1 px-2" title="Do-not-contact" onClick={() => setStatus.mutate({ id: c.id, status: 'do-not-contact' })}><Ban className="w-4 h-4 text-red-600" /></button>
                  : <button className="btn-secondary py-1 px-2" title="Un-block" onClick={() => setStatus.mutate({ id: c.id, status: 'unverified' })}><ShieldAlert className="w-4 h-4 text-amber-600" /></button>}
                <button className="btn-secondary py-1 px-2" title="Delete" onClick={() => { if (confirm(`Remove ${c.email}?`)) del.mutate(c.id) }}><Trash2 className="w-4 h-4 text-gray-400" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {form && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setForm(null)}>
          <div className="card w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold text-gray-900">Add contact</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label">Name</label><input className="input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Jane Journalist" /></div>
              <div><label className="label">Outlet</label><input className="input" value={form.outlet} onChange={e => setForm({ ...form, outlet: e.target.value })} placeholder="The Times" /></div>
            </div>
            <div><label className="label">Email *</label><input className="input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="jane@thetimes.co.uk" /></div>
            <div><label className="label">Beat / topics</label><input className="input" value={form.beat} onChange={e => setForm({ ...form, beat: e.target.value })} placeholder="cyber, AI regulation" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label">Kind</label><select className="input" value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value as 'journalist' })}><option value="journalist">Journalist</option><option value="desk">News desk</option></select></div>
              <div><label className="label">Status</label><select className="input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value as 'unverified' })}><option value="unverified">Unverified</option><option value="verified">Verified</option><option value="do-not-contact">Do-not-contact</option></select></div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button className="btn-secondary" onClick={() => setForm(null)}>Cancel</button>
              <button className="btn-primary" onClick={() => save.mutate(form)} disabled={save.isPending}>{save.isPending ? <Spinner className="w-4 h-4 border-white border-t-transparent" /> : <Check className="w-4 h-4" />} Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Sources ───────────────────────────────────────────────────────────────────
function SourcesTab({ sources, loading, userId, invalidate, note }: { sources: Source[]; loading: boolean; userId: string; invalidate: Invalidate; note: Note }) {
  const [form, setForm] = useState<{ name: string; type: Source['type']; url: string } | null>(null)

  const save = useMutation({
    mutationFn: async (f: { name: string; type: Source['type']; url: string }) => {
      if (!f.name.trim()) throw new Error('Name is required.')
      if (f.type !== 'inbound' && !f.url.trim()) throw new Error('Feed URL is required.')
      const { error } = await supabase.from('media_sources').insert({ user_id: userId, name: f.name.trim(), type: f.type, url: f.url.trim() || null, active: true })
      if (error) throw error
    },
    onSuccess: () => { invalidate('media_sources'); note('ok', 'Source added.'); setForm(null) }, onError: e => note('err', describeError(e)),
  })
  const toggle = useMutation({
    mutationFn: async (s: Source) => { const { error } = await supabase.from('media_sources').update({ active: !s.active }).eq('id', s.id).eq('user_id', userId); if (error) throw error },
    onSuccess: () => invalidate('media_sources'), onError: e => note('err', describeError(e)),
  })
  const del = useMutation({
    mutationFn: async (id: string) => { const { error } = await supabase.from('media_sources').delete().eq('id', id).eq('user_id', userId); if (error) throw error },
    onSuccess: () => { invalidate('media_sources'); note('ok', 'Source removed.') }, onError: e => note('err', describeError(e)),
  })

  if (loading) return <div className="py-16 text-center"><Spinner className="w-6 h-6 mx-auto" /></div>

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">Feeds are polled when you hit “Ingest now” (or on the ingest cron). Inbound = the ResponseSource / Qwoted forwarding inbox.</p>
        <button className="btn-secondary" onClick={() => setForm({ name: '', type: 'rss', url: '' })}><Plus className="w-4 h-4" /> Add source</button>
      </div>

      {!sources.length ? (
        <EmptyState icon={Rss} title="No sources yet" description="Click “Load UK defaults” up top to seed the standard feeds, or add your own." />
      ) : (
        <div className="card divide-y divide-gray-100">
          {sources.map(s => (
            <div key={s.id} className="flex items-center gap-3 px-4 py-3 text-sm">
              <button onClick={() => toggle.mutate(s)} className={cn('w-9 h-5 rounded-full relative transition-colors shrink-0', s.active ? 'bg-brand-600' : 'bg-gray-300')} title={s.active ? 'Active' : 'Paused'}>
                <span className={cn('absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all', s.active ? 'left-4' : 'left-0.5')} />
              </button>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2"><span className="font-medium text-gray-900">{s.name}</span><span className="badge bg-gray-100 text-gray-500">{s.type}</span></div>
                {s.url && <div className="text-xs text-gray-400 truncate">{s.url}</div>}
              </div>
              <button className="btn-secondary py-1 px-2" onClick={() => { if (confirm(`Remove ${s.name}?`)) del.mutate(s.id) }}><Trash2 className="w-4 h-4 text-gray-400" /></button>
            </div>
          ))}
        </div>
      )}

      {form && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setForm(null)}>
          <div className="card w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold text-gray-900">Add source</h3>
            <div><label className="label">Name</label><input className="input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="BBC News — Technology" /></div>
            <div><label className="label">Type</label><select className="input" value={form.type} onChange={e => setForm({ ...form, type: e.target.value as Source['type'] })}><option value="rss">RSS feed</option><option value="gnews">Google News query</option><option value="inbound">Inbound inbox</option></select></div>
            {form.type !== 'inbound' && <div><label className="label">Feed URL</label><input className="input" value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} placeholder="https://…/rss.xml" /></div>}
            <div className="flex justify-end gap-2 pt-2">
              <button className="btn-secondary" onClick={() => setForm(null)}>Cancel</button>
              <button className="btn-primary" onClick={() => save.mutate(form)} disabled={save.isPending}>{save.isPending ? <Spinner className="w-4 h-4 border-white border-t-transparent" /> : <Check className="w-4 h-4" />} Add</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Log ───────────────────────────────────────────────────────────────────────
function LogTab({ sends, loading, userId, invalidate, note }: { sends: SendRow[]; loading: boolean; userId: string; invalidate: Invalidate; note: Note }) {
  const toggle = useMutation({
    mutationFn: async ({ id, field, value }: { id: string; field: 'replied' | 'coverage_won'; value: boolean }) => {
      const { error } = await supabase.from('media_sends').update({ [field]: value }).eq('id', id).eq('user_id', userId); if (error) throw error
    },
    onSuccess: () => invalidate('media_sends'),
  })
  // Flag a bounce: mark the send undelivered AND block the address so it's never
  // reused (a dead address that keeps getting mailed hurts the sending domain).
  const markUndelivered = useMutation({
    mutationFn: async (s: SendRow) => {
      const { error } = await supabase.from('media_sends').update({ status: 'bounced' }).eq('id', s.id).eq('user_id', userId)
      if (error) throw error
      if (s.contact_id) await supabase.from('media_contacts').update({ status: 'do-not-contact', notes: `Bounced / undelivered: ${s.recipient_email}` }).eq('id', s.contact_id).eq('user_id', userId)
    },
    onSuccess: () => { invalidate('media_sends', 'media_contacts'); note('ok', 'Flagged undelivered — the address is now do-not-contact and won\'t be reused.') },
    onError: e => note('err', describeError(e)),
  })
  // Re-pitch the same story to a different recipient (the bounced one is now
  // excluded, so this resolves the next best target).
  const rePitch = useMutation({
    mutationFn: async (storyId: string) => {
      const r = await authedFetch('/api/media/draft', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ storyId, auto: true }) })
      if (!r.ok) throw new Error(await r.text())
      return r.json() as Promise<{ drafted: unknown[]; skipped: { email: string; reason: string }[] }>
    },
    onSuccess: d => { invalidate('media_drafts'); const skips = d.skipped?.length ? ` (${d.skipped.map(x => x.reason).join('; ')})` : ''; note(d.drafted?.length ? 'ok' : 'err', d.drafted?.length ? 'Re-pitched — a new draft is in Approvals.' : `No other recipient found${skips}.`) },
    onError: e => note('err', describeError(e)),
  })

  const stats = useMemo(() => {
    const sent = sends.filter(s => s.status === 'sent')
    const replies = sent.filter(s => s.replied).length
    const week = sent.filter(s => Date.parse(s.created_at) > Date.now() - 7 * 864e5).length
    return { total: sent.length, week, replies, coverage: sends.filter(s => s.coverage_won).length, rate: sent.length ? Math.round((replies / sent.length) * 100) : 0 }
  }, [sends])

  if (loading) return <div className="py-16 text-center"><Spinner className="w-6 h-6 mx-auto" /></div>

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Sent this week', value: stats.week, icon: Send },
          { label: 'Sent all-time', value: stats.total, icon: Mail },
          { label: 'Reply rate', value: `${stats.rate}%`, icon: Reply },
          { label: 'Coverage won', value: stats.coverage, icon: Award },
        ].map(s => (
          <div key={s.label} className="card p-4">
            <div className="flex items-center gap-2 text-gray-400 text-xs mb-1"><s.icon className="w-4 h-4" /> {s.label}</div>
            <div className="text-2xl font-bold text-gray-900">{s.value}</div>
          </div>
        ))}
      </div>

      {!sends.length ? (
        <EmptyState icon={Radio} title="Nothing sent yet" description="Approved pitches will be logged here — with reply and coverage tracking." />
      ) : (
        <div className="card divide-y divide-gray-100">
          {sends.map(s => (
            <div key={s.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-gray-900 truncate">{s.subject}</div>
                <div className="text-gray-500">{s.recipient_name || s.recipient_email} {s.outlet && `· ${s.outlet}`} · {stamp(s.created_at)}</div>
              </div>
              {s.status === 'failed' && <span className="badge bg-red-50 text-red-700">failed</span>}
              {s.status === 'bounced' && <span className="badge bg-red-100 text-red-800 border border-red-300">undelivered</span>}
              {s.status !== 'bounced' && (
                <button onClick={() => toggle.mutate({ id: s.id, field: 'replied', value: !s.replied })}
                  className={cn('badge border', s.replied ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-white text-gray-400 border-gray-200')}>
                  <Reply className="w-3 h-3 mr-1" /> {s.replied ? 'replied' : 'no reply'}
                </button>
              )}
              <button onClick={() => toggle.mutate({ id: s.id, field: 'coverage_won', value: !s.coverage_won })}
                className={cn('badge border', s.coverage_won ? 'bg-green-50 text-green-700 border-green-200' : 'bg-white text-gray-400 border-gray-200')}>
                <Award className="w-3 h-3 mr-1" /> {s.coverage_won ? 'coverage won' : 'mark coverage'}
              </button>
              {s.status !== 'bounced'
                ? <button onClick={() => { if (confirm(`Flag ${s.recipient_email} as undelivered and stop using it?`)) markUndelivered.mutate(s) }} disabled={markUndelivered.isPending}
                    className="badge border bg-white text-red-600 border-red-200 hover:bg-red-50"><Ban className="w-3 h-3 mr-1" /> undelivered</button>
                : s.story_id && <button onClick={() => rePitch.mutate(s.story_id!)} disabled={rePitch.isPending}
                    className="badge border bg-white text-brand-700 border-brand-200 hover:bg-brand-50">{rePitch.isPending ? <Spinner className="w-3 h-3 mr-1" /> : <Zap className="w-3 h-3 mr-1" />} re-pitch someone else</button>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
